/**
 * StaffPortalFileService
 * Service for staff file operations in the portal.
 * Route: api/staff-portal/files
 */
import { StaffPortalBaseService } from './StaffPortalBaseService';
/**
 * StaffPortalFileService
 * Handles employee file operations for the staff portal.
 * Maps to /api/staff-portal/files endpoint
 *
 * Note: Staff can only access their own files with HROnly=false.
 * HROnly files are restricted to HR administrators.
 */
export class StaffPortalFileService extends StaffPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StaffPortalFileService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? StaffPortalFileService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LIST OPERATIONS ====================
    /**
     * Get all files for the current staff member (excluding HROnly files)
     * GET /api/staff-portal/files
     */
    async getMyFiles() {
        const response = await this.getMyFilesRaw();
        return response.data ?? [];
    }
    /**
     * Get files (raw response)
     */
    getMyFilesRaw() {
        return this.get();
    }
    // ==================== GET SINGLE FILE ====================
    /**
     * Get a specific file by ID (must own the file and not HROnly)
     * GET /api/staff-portal/files/{fileId}
     */
    async getFile(fileId) {
        const response = await this.getFileRaw(fileId);
        if (!response.data) {
            throw new Error(`File ${fileId} not found`);
        }
        return response.data;
    }
    /**
     * Get file (raw response)
     */
    getFileRaw(fileId) {
        return this.get(String(fileId));
    }
    // ==================== CREATE OPERATIONS ====================
    /**
     * Upload a new file
     * Note: HROnly is forced to false for staff uploads
     * POST /api/staff-portal/files
     */
    async createFile(data) {
        const response = await this.createFileRaw(data);
        if (!response.data) {
            throw new Error('Failed to upload file');
        }
        return response.data;
    }
    /**
     * Create file (raw response)
     */
    createFileRaw(data) {
        return this.post('', data);
    }
    // ==================== DELETE OPERATIONS ====================
    /**
     * Delete a file (must own the file and not HROnly)
     * DELETE /api/staff-portal/files/{fileId}
     */
    async deleteFile(fileId) {
        const response = await this.deleteFileRaw(fileId);
        if (!response.success) {
            throw new Error('Failed to delete file');
        }
    }
    /**
     * Delete file (raw response)
     */
    deleteFileRaw(fileId) {
        return this.delete(String(fileId));
    }
}
StaffPortalFileService.DEFAULT_SERVICE_PATH = 'files';
StaffPortalFileService.BASE_PATH = 'api/staff-portal/files';
//# sourceMappingURL=StaffPortalFileService.js.map